import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getLastFetchedAt,
  getStoredBlocks,
  saveBlocks,
  setLastFetchedAt,
  type DecryptedBlock,
} from "@/lib/blockStorage";
import { decrypt } from "@/utls/CryptoHelpers";

type ApiBlock = {
  _id: string;
  updatedAt?: string;
  encryptedData?: {
    encrypted: string;
    iv: string;
    salt: string;
    tag?: string;
  };
  type?: string;
};

function mergeDecryptedBlocks({
  existing,
  incoming,
}: {
  existing: DecryptedBlock[];
  incoming: DecryptedBlock[];
}): DecryptedBlock[] {
  if (incoming.length === 0) return existing;
  const map = new Map(existing.map((b) => [b.id, b]));
  for (const b of incoming) map.set(b.id, b);
  return Array.from(map.values());
}

export type { DecryptedBlock };

type AppContextValue = {
  blocks: DecryptedBlock[];
  isLoading: boolean;
  updateDecryptedBlock: (args: { block: DecryptedBlock }) => void;
  addDecryptedBlock: (args: { block: DecryptedBlock }) => void;
  removeDecryptedBlocks: (args: { ids: string[] }) => void;
};

const AppContext = createContext<AppContextValue | null>(null);

async function loadDecryptedBlocks({
  secretKey,
  since,
}: {
  secretKey: string;
  since: string | null;
}): Promise<DecryptedBlock[]> {
  const url =
    since != null
      ? `/api/blocks?since=${encodeURIComponent(since)}`
      : "/api/blocks";
  const res = await fetch(url);
  const data = (await res.json()) as { blocks: ApiBlock[] };
  const apiBlocks = data.blocks ?? [];

  if (!secretKey) {
    return [];
  }

  const results: DecryptedBlock[] = [];
  const decryptStarted = performance.now();
  for (const block of apiBlocks) {
    if (!block.encryptedData) continue;
    try {
      const decryptedText = await decrypt(
        {
          encrypted: block.encryptedData.encrypted,
          iv: block.encryptedData.iv,
          salt: block.encryptedData.salt,
          tag: block.encryptedData.tag ?? "",
        },
        secretKey,
      );
      let decryptedParsed: unknown;
      try {
        decryptedParsed = JSON.parse(decryptedText) as unknown;
      } catch (e) {
        console.error("AppContext: JSON parse failed for block", block._id, e);
        continue;
      }
      results.push({
        id: block._id,
        data: decryptedParsed,
        type: block.type,
      });
    } catch (e) {
      console.error("AppContext: decrypt failed for block", block._id, e);
    }
  }
  console.debug(
    "AppContext: decrypt all blocks",
    `${results.length}/${apiBlocks.length} ok`,
    `${(performance.now() - decryptStarted).toFixed(1)}ms total`,
  );

  return results;
}

export function AppContextProvider({ children }: { children: ReactNode }) {
  const [blocks, setBlocks] = useState<DecryptedBlock[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isHydrated || typeof window === "undefined") return;

    saveBlocks(blocks).catch((e) => {
      console.error("AppContext: failed to persist decrypted blocks", e);
    });
  }, [blocks, isHydrated]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const stored = await getStoredBlocks();
      if (cancelled) return;
      setBlocks(stored);
      setIsHydrated(true);

      const secretKey =
        typeof window !== "undefined" ? (window.localStorage.accessKey ?? "") : "";

      if (!secretKey) {
        console.warn("AppContext: no accessKey in localStorage; skipping fetch");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const since = await getLastFetchedAt();
        const fetched = await loadDecryptedBlocks({ secretKey, since });
        if (cancelled) return;
        setBlocks((prev) => mergeDecryptedBlocks({ existing: prev, incoming: fetched }));
        try {
          await setLastFetchedAt(new Date().toISOString());
        } catch (e) {
          console.error("AppContext: failed to persist last fetch time", e);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const updateDecryptedBlock = useCallback(
    ({ block }: { block: DecryptedBlock }) => {
      setBlocks((prev) => {
        const i = prev.findIndex((b) => b.id === block.id);
        if (i === -1) return prev;
        const next = [...prev];
        next[i] = block;
        return next;
      });
    },
    [],
  );

  const addDecryptedBlock = useCallback(({ block }: { block: DecryptedBlock }) => {
    setBlocks((prev) => {
      if (prev.some((b) => b.id === block.id)) return prev;
      return [...prev, block];
    });
  }, []);

  const removeDecryptedBlocks = useCallback(({ ids }: { ids: string[] }) => {
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    setBlocks((prev) => prev.filter((b) => !idSet.has(b.id)));
  }, []);

  const value = useMemo(
    (): AppContextValue => ({
      blocks,
      isLoading,
      updateDecryptedBlock,
      addDecryptedBlock,
      removeDecryptedBlocks,
    }),
    [blocks, isLoading, updateDecryptedBlock, addDecryptedBlock, removeDecryptedBlocks],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppContext must be used within AppContextProvider");
  }
  return ctx;
}
